// @ts-strict-ignore
import type { JourneyTypes } from '@usealloy/typegen';
import { JourneyTypesMap } from '@usealloy/typegen';

import { OUTCOMES_API_MAX_LIMIT } from '~/dashboard/views/settings/outcomes/hooks';
import type { Outcome } from '~/dashboard/views/settings/outcomes/types';
import apiCalls from '~/helpers/apiCalls';

const { settings: { getOutcomesNew: getOutcomes } } = apiCalls;

export const getOutcomeNameForJourneyAction = (outcome?: string | null): string => (
  (outcome ?? '').split(' ').join('_').toUpperCase()
);

export const APPLICATION_OUTCOMES = ['Approved', 'Denied'] as const;
export const ALERT_OUTCOMES = ['Dismissed', 'Suspicious'] as const;
export const MANUAL_REVIEW_OUTCOME = 'Manual Review';
export const WITHDRAWN_OUTCOME = 'Withdrawn';

/**
 * The standard Manual Review node should only ever have the default outcomes based on journey type
 * - Approved/Denied/Withdrawn (if relevant) for origination
 * - Dismissed/Suspicious for ongoing
 *
 * Custom outcomes for manual reviews can be configured using custom review nodes
 */
export const ALLOWED_APPLICATION_MANUAL_REVIEW_OUTCOMES = [
  ...APPLICATION_OUTCOMES,
  WITHDRAWN_OUTCOME,
] as const;
export const ALLOWED_ALERT_MANUAL_REVIEW_OUTCOMES = [...ALERT_OUTCOMES] as const;

export const OUTCOME_DISPLAY_NAMES_BY_OUTCOME = {
  APPROVED: 'Approved',
  DENIED: 'Denied',
  EXPIRED: 'Expired',
  MANUAL_REVIEW: MANUAL_REVIEW_OUTCOME,
  RETRIES_EXCEEDED: 'Retries Exceeded',
  RETRY: 'Retry',
} as const;

export interface OutcomesLists {
  eventStatusOutcomes: Outcome[];
  manualReviewOutcome: Outcome | null;
  journeyOutcomes: Outcome[];
  workflowAndActionOutcomes: Outcome[];
}

export const getAllowedJourneyOutcomes = async ({
  journeyType,
  typhonDomain,
}: {
  journeyType: JourneyTypes;
  typhonDomain: string;
}) => {
  const filterField = journeyType === JourneyTypesMap.APPLICATION
    ? 'is_application_outcome'
    : 'is_alert_outcome';

  /**
   * Journey Applications and Alerts store outcomes by name rather than DB id. So
   * we need to dedupe any custom outcomes from the default system outcomes,
   * case-insensitively.
   */
  const dedupeOutcomeArray = (
    journeyType === JourneyTypesMap.APPLICATION
      ? [...APPLICATION_OUTCOMES]
      : [...ALERT_OUTCOMES]
  ).map(name => name.toLowerCase());

  const { outcomes }: { outcomes: Outcome[] } = await getOutcomes({
    options: {
      numRowsPerPage: OUTCOMES_API_MAX_LIMIT,
      q: {
        includeEventStatusOutcomes: true,
      },
    },
    typhonDomain,
  });

  const outcomesLists: OutcomesLists = outcomes.reduce((agg, outcome) => {
    if (outcome[filterField]) {
      if (
        outcome.is_system_outcome
        || (
          !outcome.archived
          && !dedupeOutcomeArray.includes(outcome.outcome.toLowerCase())
        )
      ) {
        agg.journeyOutcomes.push(outcome);
      }
    }

    if (outcome.is_event_status) {
      agg.eventStatusOutcomes.push(outcome);
    }

    if (outcome.is_workflow_outcome) {
      agg.workflowAndActionOutcomes.push(outcome);
    }

    if (
      outcome.outcome?.toLowerCase() === MANUAL_REVIEW_OUTCOME.toLowerCase()
      // We only want the one system `Manual Review` outcome for this
      && outcome.is_system_outcome
    ) {
      agg.manualReviewOutcome = outcome;
    }

    return agg;
  }, {
    eventStatusOutcomes: [],
    journeyOutcomes: [],
    manualReviewOutcome: null,
    workflowAndActionOutcomes: [],
  });

  return outcomesLists;
};

export function getOutcomesToDisplay({
  isDefaultReviewNode = false,
  isOngoingJourney,
  journeyOutcomes,
}: {
  isDefaultReviewNode?: boolean;
  isOngoingJourney: boolean;
  journeyOutcomes: Outcome[];
}): string[] {
  /**
     * The standard Manual Review node should only ever have the default outcomes based on Journey type
     * (Approved/Denied/Withdrawn (if relevant) for origination, Dismissed/Suspicious for ongoing)
     *
     * Custom outcomes for manual reviews can be configured using custom review nodes
     */
  if (isDefaultReviewNode) {
    const hasWithdrawnOutcome = journeyOutcomes.some(
      outcome => outcome.outcome === WITHDRAWN_OUTCOME,
    );

    return isOngoingJourney
      ? [...ALERT_OUTCOMES]
      : [
        ...APPLICATION_OUTCOMES,
        ...(hasWithdrawnOutcome ? [WITHDRAWN_OUTCOME] : []),
      ];
  }

  const outcomes = journeyOutcomes.map(outcome => outcome.outcome);

  const hasManualReview = outcomes.some(
    outcome => outcome.toLowerCase() === MANUAL_REVIEW_OUTCOME.toLowerCase(),
  );

  if (!isOngoingJourney && !hasManualReview) {
    outcomes.push(MANUAL_REVIEW_OUTCOME);
  }

  return outcomes;
}
